import IconButton from 'Components/Buttons/IconButton';
import Circle from 'Components/Circle';
import Panel from 'Components/Panel';
import SimpleList from 'Components/SimpleList';
import { actions as deviceActions } from 'ducks/devices';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import icons from 'utils/ui/icons';

const copyToClipboard = str => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(str);
    }

    return Promise.reject('The Clipboard API is not available.');
};

// Whether the URL answers, not whether it was switched on: Balena's proxy reaches the box through its
// VPN, so a URL left on with the VPN down is still unavailable.
const generateUrl = (reachable, device) => {
    let link = 'unavailable';
    let color = '#B94A48';
    if (reachable) {
        link = <a href={`https://${device.balena_uuid}.balena-devices.com`} rel="noreferrer" target="_blank">{device.balena_uuid}</a>;
        color = '#2C6700';
    }

    return (
        <>
            <Circle color={color}/>
            &nbsp;
            {link}
        </>
    );
};

// Only the device's own endpoint carries the web password, and any other answer about the device
// replaces it without one. Rather than chase every such answer, the password is asked for when wanted.
const WebPassword = props => {
    const { onReveal, password } = props;

    const [copied, setCopied] = useState(false);

    if (!password) {
        return (
            <>
                ••••••••
                &nbsp;
                <IconButton
                    icon={icons.eye}
                    onClick={onReveal}
                    size="lg"
                    style={{ marginLeft: -1, marginTop: -2, padding: 5 }}
                    tooltip="Show password"
                />
            </>
        );
    }

    const copyPassword = () => {
        copyToClipboard(password);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <>
            {password}
            &nbsp;
            <IconButton
                icon={icons.clipboard}
                onClick={copyPassword}
                size="lg"
                style={{ marginLeft: -1, marginTop: -2, padding: 5 }}
                tooltip="click to copy"
            />
            {copied ? ' copied to clipboard!' : null}
        </>
    );
};

const state = balenaStatus => {
    let message = 'unknown';
    let color = '#888888';
    if (balenaStatus) {
        message = balenaStatus['state'];
        if (balenaStatus['state'] === 'Online') {
            color = '#2C6700';
        } else if (balenaStatus['state'] === 'Offline') {
            color = '#B94A48';
        } else {
            color = '#E59400';
        }
    }

    return (
        <>
            <Circle color={color}/>
            &nbsp;
            {message}
        </>
    );
};

const isVpnActivated = balenaStatus => balenaStatus ? balenaStatus['vpn_variable'] : false;

const isPublicUrlActivated = balenaStatus => balenaStatus ? balenaStatus['public_url'] : false;

const BalenaDetails = props => {
    const { balenaStatus, device, submitting } = props;

    const vpnActive = isVpnActivated(balenaStatus);
    const publicUrlActive = isPublicUrlActivated(balenaStatus);

    const dispatch = useDispatch();
    const { getBalenaStatus, togglePublicUrlOff, togglePublicUrlOn, toggleVpnOff, toggleVpnOn } = ({
        getBalenaStatus: id => dispatch(deviceActions.getBalenaStatus(id)),
        togglePublicUrlOff: id => dispatch(deviceActions.togglePublicUrlOff(id)),
        togglePublicUrlOn: id => dispatch(deviceActions.togglePublicUrlOn(id)),
        toggleVpnOff: id => dispatch(deviceActions.toggleVpnOff(id)),
        toggleVpnOn: id => dispatch(deviceActions.toggleVpnOn(id))
    });

    // Balena keeps both switches in the cloud and the box applies them when it next checks in, so
    // neither waits on the box being online nor on the other: a toggle is held back only while its
    // own value is not known yet, or while a request is in flight.
    const VpnSection = (
        <>
            <IconButton
                disabled={balenaStatus?.vpn_variable === undefined || submitting}
                icon={icons[vpnActive ? 'off' : 'on']}
                onClick={() => {
                    vpnActive ? toggleVpnOff(device.id) : toggleVpnOn(device.id);
                }}
                style={{ marginLeft: -3, padding: 3 }}
                tooltip={`Toggle ${vpnActive ? 'Off' : 'On'}`}
            />
            {submitting ? 'processing' : null}
        </>
    );

    const PublicUrlSection = (
        <>
            <IconButton
                disabled={balenaStatus?.public_url === undefined || submitting}
                icon={icons[publicUrlActive ? 'off' : 'on']}
                onClick={() => {
                    publicUrlActive ? togglePublicUrlOff(device.id) : togglePublicUrlOn(device.id);
                }}
                style={{ marginLeft: -3, padding: 3 }}
                tooltip={`Toggle ${publicUrlActive ? 'Off' : 'On'}`}
            />
            {submitting ? 'processing' : null}
        </>
    );

    const buttons = [
        {
            icon: icons.refresh,
            onClick: () => getBalenaStatus(device.id),
            submitting: submitting,
            tooltip: 'Refresh balena status'
        }
    ];

    const balenaAttributes = [
        { label: 'VPN (change might take up to 15 minutes to take effect)', content: VpnSection },
        { label: 'Public Device URL enable/disable', content: PublicUrlSection },
        { label: 'Public Device URL', content: generateUrl(balenaStatus?.manageable, device) }
    ];

    if (!submitting) {
        balenaAttributes.push(
            { label: 'State', content: state(balenaStatus) },
            { label: 'Web Password', content: <WebPassword onReveal={() => dispatch(deviceActions.fetchOne(device.id))} password={device.web_password}/> }
        );
    }

    return (
        <Panel buttons={buttons}>
            <SimpleList content={balenaAttributes}/>
        </Panel>
    );
};

export default BalenaDetails;
